"use client";

import Image from "next/image";
import Link from "next/link";
import { User as UserIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { DockSheet } from "@/components/ui/dock-sheet";
import { DialogCard } from "@/components/ui/dialog-card";
import { Button } from "@/components/ui/button";
import { useMobile } from "@/hooks/use-mobile";
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets";

interface AuthModalProps {
  isOpen: boolean;
  onCloseAction: () => void;
  username?: string;
  userImage?: string;
  message?: string;
  callbackUrl?: string;
  title?: string;
}

export default function AuthModal({
  isOpen,
  onCloseAction,
  username = "",
  userImage,
  message = "Please log in or register to continue",
  callbackUrl,
  title,
}: AuthModalProps) {
  const t = useTranslations("authModal");
  const tCommon = useTranslations("common");
  const [imgError, setImgError] = useState(false);
  const router = useRouter();
  const isMobile = useMobile();

  // `h-16 w-16` box below -> SQUARE_SM. `userImage` arrives already resolved
  // (every caller runs it through `getAvatarUrl`) but NOT resized, so this is
  // the one place ~8 call sites get their size. `quality` dropped: the preset
  // already encodes 60 at the Supabase layer, and re-encoding an already-lossy
  // 192 px source at 100 produces a LARGER file (handoff §5) on a second,
  // separately billed cache key.
  const avatar = userImage && !imgError ? (
    <Image
      src={imagePresetUrl(userImage, SQUARE_SM)}
      alt={username || "User"}
      fill
      // `unoptimized` on a preset URL: the src is already exactly 192 device px
      // from the CDN, so there is nothing for the Next optimizer to negotiate
      // and a second pass would be a billed transformation for the same pixels.
      //
      // And therefore NO `sizes`, which this element used to carry as "64px".
      // `sizes` only selects from a `srcSet`, and `unoptimized` makes
      // next/image return `{ srcSet: undefined, sizes: undefined }`
      // (`next/dist/shared/lib/get-img-props.js::generateImgAttrs`) — the
      // attribute never reached the DOM. Next agrees: its own "fill without
      // sizes" dev warning is gated on `!unoptimized`. Adding it back would
      // satisfy the `fill`/`sizes` gate with an attribute that does nothing,
      // which is the exact pattern DEAD_SIZES_RULE now fails.
      unoptimized
      className="object-cover"
      onError={() => setImgError(true)}
    />
  ) : (
    <UserIcon className="h-8 w-8 text-muted-foreground" />
  );

  const isQuestionContext =
    message?.toLowerCase().includes("ask a question") ||
    message?.toLowerCase().includes("connect to ask");
  const dialogTitle =
    title ??
    (isQuestionContext && username
      ? t("connectWith", { username })
      : isQuestionContext
        ? t("connect")
        : username
          ? t("seeMore", { username })
          : t("joinKraahl"));
  const dialogMessage =
    isQuestionContext && username ? t("askQuestion", { username }) : message;

  const resolvedCallbackUrl =
    callbackUrl ??
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/");

  // Chrome-free by construction: the close control belongs to whichever
  // primitive renders this body (`DialogCard` on desktop, `DockSheet` on
  // mobile), and neither the padding nor the close button is decided here any
  // more. Drawing the close INSIDE this shared body is exactly what forced the
  // sheet into `chrome="none"` — HANDOFF_DIALOG_CHROME §3.
  const content = (
    <div className="flex flex-col items-center gap-6">
      <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-foreground/10 bg-foreground/10">
        {avatar}
      </div>

      <div className="space-y-2 text-center">
        <h2 className="text-xl font-semibold text-foreground">{dialogTitle}</h2>
        <p className="text-sm text-muted-foreground text-pretty">{dialogMessage}</p>
      </div>

      <div className="w-full space-y-3">
        <Button
          variant="foil"
          className="h-12 w-full rounded-full text-base font-semibold"
          size="lg"
          onClick={() => {
            router.push(
              `/register?callbackUrl=${encodeURIComponent(resolvedCallbackUrl)}`,
            );
            onCloseAction();
          }}
        >
          {t("register")}
        </Button>
        <Button
          variant="outline"
          className="h-12 w-full rounded-full border-foreground/20 bg-transparent text-base font-semibold text-foreground hover:bg-foreground/10 hover:text-foreground"
          size="lg"
          onClick={() => {
            router.push(
              `/login?callbackUrl=${encodeURIComponent(resolvedCallbackUrl)}`,
            );
            onCloseAction();
          }}
        >
          {t("logIn")}
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground/70">
        {tCommon.rich("agreeTerms", {
          privacy: (chunks) => (
            <Link
              href="/privacy"
              className="underline hover:text-foreground"
              onClick={onCloseAction}
            >
              {chunks}
            </Link>
          ),
          terms: (chunks) => (
            <Link
              href="/terms"
              className="underline hover:text-foreground"
              onClick={onCloseAction}
            >
              {chunks}
            </Link>
          ),
        })}
      </p>
    </div>
  );

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <DockSheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onCloseAction();
        }}
        // The body opens with its own hero (avatar + `text-xl` heading), so a
        // chrome title would be a second one — handle + close only. Mirror of
        // the desktop `chrome="closeOnly"` below.
        chrome="handleOnly"
        contentSized
        zIndex={10060}
      >
        {content}
      </DockSheet>
    );
  }

  return (
    <DialogCard
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onCloseAction();
      }}
      chrome="closeOnly"
      a11yTitle={dialogTitle}
      panelClassName="max-w-sm"
    >
      {content}
    </DialogCard>
  );
}
